import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { addRecaptchaToken, OTP_RECAPTCHA_ACTION } from '../Utils/recaptcha';
import {
  getAppointmentType,
  getWebSettings,
  getCountries,
  getServicesCategoryWithTimeCache,
  getOutletsWithTimeCache,
  getOutletTypeWithTimeCache,
  getServiceAddonsWithTimeCache,
  getOutletTimingWithTimeCache,
  getStaffWithTimeCache,
  getTimeSlotsWithTimeCache,
  getTimeSlotsBasedOnServicesWithTimeCache,
  getBookingDatesWithTimeCache,
  getBookingSlots,
  checkBookingSlots,
  checkSlotAvailable,
  getSelectedOutlet,
  getVouchersListWithTimeCache,
  postBookingData,
  cancelBooking,
  rescheduleBooking,
  postFormData,
  checkUser,
  getCustomerDetails,
  postSMSData,
  postWhatsAppData,
  postOTP,
  postRecaptchaVerify,
  postVoucherOrder,
  saveVoucherOrder,
  getVoucherReceipt,
} from './APICalling';
import {
  NO_RETRY,
  retryDelay,
  shouldRetrySafeMutation,
} from './retryPolicy';
import logger from '../Utils/logger';
import { normalizeCountriesResponse } from '../Utils/countries';
import { releaseBookingHoldOnce, resetBookingHoldRelease } from '../Utils/bookingHold';

// One shared directory for phone inputs and booking country IDs. No per-input requests.
export const useGetCountries = () => useQuery({
  queryKey: ['countries'],
  queryFn: async () => normalizeCountriesResponse(await getCountries()),
  staleTime: 24 * 60 * 60 * 1000,
  gcTime: 24 * 60 * 60 * 1000,
  refetchOnMount: true,
  throwOnError: false,
});

const withRetryLogging = (retryFn) => (failureCount, error) => {
  const should = retryFn(failureCount, error);
  if (should) {
    logger.debug('React Query retry attempt', {
      attempt: failureCount + 1,
      message: error?.message,
      status: error?.response?.status,
    });
  }
  return should;
};

/**
 * React Query Hooks for API Calls
 *
 * Phase 1: Safe read-only queries ✅ COMPLETE
 * - useGetAppointmentType: Booking types
 *
 * Phase 2: Static/Semi-static data (7 days & 1 day caches)
 * - useGetServiceCategories: Service categories with services
 * - useGetOutlets: List of outlets
 * - useGetOutletType: Outlet types
 * - useGetServiceAddons: Service addons list
 * - useGetOutletTiming: Outlet timings
 *
 * Design:
 * - Wraps existing APICalling functions
 * - Keeps old functions intact for fallback
 * - Conservative caching defaults
 * - No business logic changes
 */

// ============================================
// PHASE 1: Safe Read-Only Queries ✅
// ============================================

/**
 * Get appointment types (booking types)
 */
export const useGetAppointmentType = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['appointmentTypes', params],
    queryFn: () => getAppointmentType(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
};

/**
 * Get web settings (logo, background, titles, deposit/limits)
 * Signed image URLs expire (~12h), so keep cache shorter than that.
 */
export const useGetWebSettings = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['webSettings', params],
    queryFn: () => getWebSettings(params),
    staleTime: 60 * 60 * 1000,      // 1 hour
    gcTime: 2 * 60 * 60 * 1000,     // 2 hours
    ...options,
  });
};

// ============================================
// PHASE 2: Static Data (7-day cache)
// ============================================

/**
 * Get service categories with services
 * Replaces: getServicesCategoryWithTimeCache
 * TTL: 7 days (rarely changes)
 */
export const useGetServiceCategories = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['serviceCategories', params],
    queryFn: () => getServicesCategoryWithTimeCache(params),
    staleTime: 7 * 24 * 60 * 60 * 1000,  // 7 days
    gcTime: 7 * 24 * 60 * 60 * 1000,     // 7 days
    ...options,
  });
};

/**
 * Get vouchers list (online listing)
 * Replaces: Direct getVouchersList call
 * TTL: 7 days (rarely changes)
 */
export const useGetVouchersList = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['vouchersList', params],
    queryFn: () => getVouchersListWithTimeCache(params),
    staleTime: 7 * 24 * 60 * 60 * 1000,  // 7 days
    gcTime: 7 * 24 * 60 * 60 * 1000,     // 7 days
    ...options,
  });
};

/**
 * Get outlets list
 * Replaces: getOutletsWithTimeCache
 * TTL: 7 days (rarely changes)
 */
export const useGetOutlets = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['outlets', params],
    queryFn: () => getOutletsWithTimeCache(params),
    staleTime: 7 * 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    ...options,
  });
};

/**
 * Get single outlet details by ID
 * Replaces: getSelectedOutlet (direct call)
 * TTL: 7 days (outlet details rarely change)
 */
export const useGetSelectedOutlet = (outletID, params = {}, options = {}) => {
  return useQuery({
    queryKey: ['outlet', outletID, params],
    queryFn: () => getSelectedOutlet(outletID, params),
    staleTime: 7 * 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    enabled: !!outletID && outletID !== 'null',
    ...options,
  });
};

/**
 * Get outlet types
 * Replaces: getOutletTypeWithTimeCache
 * TTL: 7 days (rarely changes)
 */
export const useGetOutletType = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['outletTypes', params],
    queryFn: () => getOutletTypeWithTimeCache(params),
    staleTime: 7 * 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    ...options,
  });
};

/**
 * Get service addons
 * Replaces: getServiceAddonsWithTimeCache
 * TTL: 7 days (rarely changes)
 */
export const useGetServiceAddons = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['serviceAddons', params],
    queryFn: () => getServiceAddonsWithTimeCache(params),
    staleTime: 7 * 24 * 60 * 60 * 1000,
    gcTime: 7 * 24 * 60 * 60 * 1000,
    ...options,
  });
};

// ============================================
// PHASE 2: Semi-Static Data (1 day cache)
// ============================================

/**
 * Get outlet timings
 * Replaces: getOutletTimingWithTimeCache
 * TTL: 1 day (changes weekly-monthly)
 */
export const useGetOutletTiming = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['outletTimings', params],
    queryFn: () => getOutletTimingWithTimeCache(params),
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    ...options,
  });
};

/**
 * Get staff/therapists
 * Fresh fetch (no caching) - availability changes frequently
 */
export const useGetStaff = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['staff', params],
    queryFn: () => getStaffWithTimeCache(params),
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    ...options,
  });
};

// ============================================
// PHASE 2: Dynamic Data (< 1 hour cache)
// ============================================

/**
 * Get time slots for therapist
 * Replaces: getTimeSlotsWithTimeCache
 * TTL: 30 minutes (changes hourly)
 */
export const useGetTimeSlots = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['timeSlots', params],
    queryFn: () => getTimeSlotsWithTimeCache(params),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    ...options,
  });
};

/**
 * Get time slots based on services
 * Replaces: getTimeSlotsBasedOnServicesWithTimeCache
 * TTL: 30 minutes (changes hourly)
 */
export const useGetTimeSlotsBasedOnServices = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['timeSlotsServices', params],
    queryFn: () => getTimeSlotsBasedOnServicesWithTimeCache(params),
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    ...options,
  });
};

/**
 * Get booking dates (POST JSON body, same as Postman)
 * Replaces: getBookingDatesWithTimeCache
 * TTL: 1 hour (changes daily)
 */
export const useGetBookingDates = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['bookingDates', params],
    queryFn: () => getBookingDatesWithTimeCache(params),
    staleTime: 60 * 60 * 1000,
    gcTime: 2 * 60 * 60 * 1000,
    ...options,
  });
};

/**
 * Get available booking slots (POST JSON body, same as Postman)
 * Replaces: getBookingSlots (direct call)
 * TTL: 30 minutes (available slots change hourly)
 */
export const useGetBookingSlots = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['bookingSlots', params],
    queryFn: () => getBookingSlots(params),
    // staleTime: 30 * 60 * 1000,
    staleTime:0,
    // gcTime: 60 * 60 * 1000,
    // gcTime: 60 * 60 * 1000,
    gcTime: 0,
    ...options,
  });
};

/**
 * Check whether a specific slot is still available (POST JSON body).
 * Same payload as getBookingSlots, plus `slot` (e.g. "06:00PM").
 */
export const useCheckBookingSlots = (options = {}) => {
  return useMutation({
    mutationFn: (data) => checkBookingSlots(data),
    retry: NO_RETRY,
    ...options,
  });
};

/**
 * Check whether a slot is available before entering the booking flow.
 * Endpoint: POST /outlet-timings/check/slot-available
 */
export const useCheckSlotAvailable = (options = {}) => {
  return useMutation({
    mutationFn: (data) => checkSlotAvailable(data),
    retry: NO_RETRY,
    ...options,
  });
};

// ============================================
// PHASE 3: Mutations (Create/Update/Delete)
// ============================================

/**
 * Create a new booking
 * Replaces: Direct postBookingData call
 * Auto-invalidates: bookings, bookingDates, timeSlots
 */
export const useCreateBooking = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await postBookingData(data);
      if (response?.data?.success) {
        resetBookingHoldRelease(data.uuid);
        resetBookingHoldRelease(response?.data?.data?.booking?.uuid);
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookingDates'] });
      queryClient.invalidateQueries({ queryKey: ['timeSlots'] });
    },
    ...options,
    // No auto-retry: no idempotency key — duplicate holds/bookings risk
    retry: NO_RETRY,
  });
};

/**
 * Cancel an existing booking
 * Replaces: Direct cancelBooking call
 * Auto-invalidates: bookings, bookingDates
 */
export const useCancelBooking = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => cancelBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookingDates'] });
    },
    ...options,
    retry: NO_RETRY,
  });
};

/**
 * Reschedule an existing booking
 * Replaces: Direct rescheduleBooking call
 * Auto-invalidates: bookings, bookingDates, timeSlots
 */
export const useRescheduleBooking = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => rescheduleBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookingDates'] });
      queryClient.invalidateQueries({ queryKey: ['timeSlots'] });
    },
    ...options,
    retry: NO_RETRY,
  });
};

/**
 * Create a new user/form submission
 * Replaces: Direct postFormData call
 * Auto-invalidates: user-related queries
 */
export const useCreateUser = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => postFormData(data),
    // Used for OTP resend — safe limited retry on network/5xx
    retry: withRetryLogging(shouldRetrySafeMutation),
    retryDelay,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    ...options,
  });
};

/**
 * Check/verify user during booking flow
 * Replaces: Direct checkUser call
 * Auto-invalidates: user verification queries
 */
export const useCheckUser = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => checkUser(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userCheck'] });
    },
    ...options,
    // Creates/links user in booking flow — avoid duplicate side effects
    retry: NO_RETRY,
  });
};

/**
 * Send SMS OTP
 * Replaces: Direct postSMSData call
 * Auto-invalidates: otp-related queries
 */
export const useSendSMS = (options = {}) => {
  const queryClient = useQueryClient();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return useMutation({
    mutationFn: async (data) => postSMSData(
      await addRecaptchaToken(executeRecaptcha, data, OTP_RECAPTCHA_ACTION)
    ),
    // Each send consumes a server-side attempt; do not retry automatically.
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otp'] });
    },
    ...options,
  });
};

/**
 * Send WhatsApp OTP
 * Auto-invalidates: otp-related queries
 */
export const useSendWhatsApp = (options = {}) => {
  const queryClient = useQueryClient();
  const { executeRecaptcha } = useGoogleReCaptcha();

  return useMutation({
    mutationFn: async (data) => postWhatsAppData(
      await addRecaptchaToken(executeRecaptcha, data, OTP_RECAPTCHA_ACTION)
    ),
    // Sends consume attempts and CAPTCHA tokens; retries need a fresh user action.
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otp'] });
    },
    ...options,
  });
};

/**
 * Verify a reCAPTCHA token
 * Replaces: Direct postRecaptchaVerify call
 * Tokens are single-use, so a failed attempt should not be retried with the same token.
 */
export const useVerifyRecaptcha = (options = {}) => {
  return useMutation({
    mutationFn: (data) => postRecaptchaVerify(data),
    retry: NO_RETRY,
    ...options,
  });
};

/**
 * Verify OTP
 * Replaces: Direct postOTP call
 * Auto-invalidates: otp verification, auth
 */
export const useVerifyOTP = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => postOTP(data),
    // Verification consumes an attempt: only an explicit user action may retry.
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['otp'] });
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
    ...options,
  });
};

export const useGetCustomerDetails = (options = {}) => {
  return useMutation({
    mutationFn: (data) => getCustomerDetails(data),
    retry: false,
    ...options,
  });
};

/**
 * Delete a booking
 * Replaces: Direct deleteBooking call
 * Auto-invalidates: bookings, bookingDates
 */
export const useDeleteBooking = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uuid) => releaseBookingHoldOnce(uuid),
    // Hold release — generally safe to retry once on network/5xx
    retry: withRetryLogging(shouldRetrySafeMutation),
    retryDelay,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      queryClient.invalidateQueries({ queryKey: ['bookingDates'] });
    },
    ...options,
  });
};

/**
 * Create a voucher order
 * Replaces: Direct postVoucherOrder call
 * No auto-retry: order creation is not idempotent (could create duplicates)
 */
export const useCreateVoucherOrder = (options = {}) => {
  return useMutation({
    mutationFn: (data) => postVoucherOrder(data),
    retry: NO_RETRY,
    ...options,
  });
};

/** Reuse the existing order when billing details or voucher quantities change. */
export const useSaveVoucherOrder = (options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: saveVoucherOrder,
    retry: NO_RETRY,
    onSuccess: (response, variables, ...rest) => {
      if (variables.orderId != null) {
        queryClient.invalidateQueries({ queryKey: ['voucherReceipt', variables.orderId] });
      }
      return options.onSuccess?.(response, variables, ...rest);
    },
  });
};

/**
 * Fetch voucher order receipt
 * Replaces: Direct getVoucherReceipt call
 * Auto-caches: Receipt data with 5-minute stale time
 */
export const useGetVoucherReceipt = (orderId, options = {}) => {
  return useQuery({
    queryKey: ['voucherReceipt', orderId],
    queryFn: () => getVoucherReceipt(orderId),
    enabled: !!orderId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    ...options,
  });
};
